export default {
  namespaced: true,

  state: {
    messages: [],
    groupMembers: [],
    mutedUsers: [],
    mutedUsersLoaded: false,
    currentUserMuted: false,
    groupNotice: '',
    productCardInfo: null,
    liveStatus: {
      connected: false,
      groupJoined: false,
      loading: false,
      error: null
    },
    likeCount: 0, // 点赞数统计
    viewCount: 0, // 浏览量PV统计
    onlineUsers: [],
    onlineUserCount: 0,
    // 直播统计 WebSocket 推送的实时数据
    // uv: 正在看的人（原有字段保留，暂不展示）
    // pv: 看过的人
    // ou: 正在看的人（新字段，用于展示）
    // virtualOnlineCount: 详情接口返回的虚拟在线数，用于普通用户 pv 放大展示
    socketStats: { uv: 0, pv: 0, ou: 0, liveId: '', virtualOnlineCount: 0 },
    isBigGroup: false,
    roomGoodsTotal: 0,
    // 管理员/运营消息历史分页状态（listMessage 全部群发消息分页）
    messageHistory: {
      nextPageToken: '', // 下一页 token，'' 表示第一页未加载或已重置
      hasMore: true, // 是否还有更早的消息
      loading: false, // 是否正在加载
      initialized: false // 首页是否已加载
    },
    microphoneVolume: 1.0, // 麦克风音量，范围 0-1
    isMirrorEnabled: false, // 是否启用前置摄像头镜像
    roomInfo: {
      groupMuted: false,
      anchorName: '',
      anchorAvatar: ''
    }
  },

  getters: {
    getAllMessages: state => state.messages,

    getVisibleMessagesFn: state => (maxCount = 50) => {
      const total = state.messages.length
      if (total <= maxCount) {
        return state.messages
      }
      return state.messages.slice(total - maxCount, total)
    },

    getGroupMembers: state => state.groupMembers,

    getMutedUsers: state => state.mutedUsers,

    getMutedUsersLoaded: state => state.mutedUsersLoaded,

    isUserMutedFn: state => userId => {
      return state.mutedUsers.some(
        user => String(user.userId) === String(userId)
      )
    },

    getOnlineUsers: state => {
      return state.onlineUsers.map(user => ({
        userId: user.userId || '',
        nickName: user.nickName || user.nickname || '用户',
        nickname: user.nickname || user.nickName || '用户',
        userAvatar: user.userAvatar || user.avatar || '',
        avatar: user.avatar || user.userAvatar || '',
        role: user.role || 'user'
      }))
    },

    getOnlineUserCount: state => state.onlineUserCount,
    getViewCount: state => state.viewCount,
    getIsBigGroup: state => state.isBigGroup,

    getChatStats: state => {
      const totalMessages = state.messages.length
      const userMessages = state.messages.filter(msg => !msg.isSystem).length
      const systemMessages = state.messages.filter(msg => msg.isSystem).length
      const onlineCount = state.onlineUserCount
      const likeCount = state.likeCount
      const viewCount = state.viewCount
      return {
        totalMessages,
        userMessages,
        systemMessages,
        onlineCount,
        likeCount,
        viewCount
      }
    },
    // 直播统计 WebSocket 数据：uv=正在看的人（旧），ou=正在看的人（新，用于展示），pv=看过的人，virtualOnlineCount=虚拟在线数
    getSocketStats: state => ({
      uv: state.socketStats.uv || 0,
      pv: state.socketStats.pv || 0,
      ou: state.socketStats.ou || 0,
      virtualOnlineCount: state.socketStats.virtualOnlineCount || 0
    }),
    getMicrophoneVolume: state => state.microphoneVolume,
    getMirrorEnabled: state => state.isMirrorEnabled
  },

  mutations: {
    // 在线用户相关
    SET_ONLINE_USERS(state, users) {
      state.onlineUsers = users.map(user => ({
        userId: user.userId || '',
        nickName: user.nickName || user.nickname || '用户',
        nickname: user.nickname || user.nickName || '用户',
        userAvatar: user.userAvatar || user.avatar || '',
        avatar: user.avatar || user.userAvatar || '',
        role: user.role || 'user'
      }))
    },
    SET_ONLINE_USER_COUNT(state, count) {
      state.onlineUserCount = count
    },
    SET_VIEW_COUNT(state, count) {
      state.viewCount = count
    },
    // 直播统计 WebSocket 推送（uv 正在看的人/ pv 看过的人/ ou 正在看的人新字段）
    // 注意：仅更新 uv/pv/ou/liveId，保留 virtualOnlineCount（来自详情接口，不随 WS 推送重置）
    SET_SOCKET_STATS(state, payload) {
      state.socketStats = {
        ...state.socketStats,
        uv: payload && payload.uv !== undefined ? payload.uv : 0,
        pv: payload && payload.pv !== undefined ? payload.pv : 0,
        ou: payload && payload.ou !== undefined ? payload.ou : 0,
        liveId: payload && payload.liveId !== undefined ? payload.liveId : ''
      }
    },
    // 虚拟在线数（来自直播间详情接口 res.data.virtualOnlineCount），用于放大普通用户看到的 pv
    SET_VIRTUAL_ONLINE_COUNT(state, count) {
      state.socketStats = {
        ...state.socketStats,
        virtualOnlineCount: Number(count) || 0
      }
    },
    SET_IS_BIG_GROUP(state, isBigGroup) {
      state.isBigGroup = isBigGroup
    },
    ADD_ONLINE_USER(state, user) {
      if (!state.onlineUsers.some(u => u.userId === user.userId)) {
        state.onlineUsers.push({
          userId: user.userId || '',
          nickName: user.nickName || user.nickname || '用户',
          nickname: user.nickname || user.nickName || '用户',
          userAvatar: user.userAvatar || user.avatar || '',
          avatar: user.avatar || user.userAvatar || '',
          role: user.role || 'user'
        })
      }
    },
    REMOVE_ONLINE_USER(state, userId) {
      state.onlineUsers = state.onlineUsers.filter(u => u.userId !== userId)
    },

    // 消息相关
    SET_MESSAGES(state, messages) {
      state.messages = messages
    },
    ADD_MESSAGE(state, message) {
      if (state.messages.length >= 1000) {
        state.messages = state.messages.slice(-500)
      }
      state.messages.push(message)
    },
    ADD_MESSAGES(state, messages) {
      state.messages.push(...messages)
    },
    // 在数组头部插入更早的历史消息（管理员上滑分页加载）
    PREPEND_MESSAGES(state, messages) {
      state.messages.unshift(...messages)
      // 上限保护：admin 深度浏览时避免无限增长，超过 2000 条裁掉最旧的一半
      if (state.messages.length > 2000) {
        state.messages = state.messages.slice(-1000)
      }
    },
    // 更新消息历史分页状态
    SET_MESSAGE_HISTORY_STATE(state, payload) {
      state.messageHistory = { ...state.messageHistory, ...payload }
    },
    // 重置消息历史分页状态
    RESET_MESSAGE_HISTORY(state) {
      state.messageHistory = {
        nextPageToken: '',
        hasMore: true,
        loading: false,
        initialized: false
      }
    },
    UPDATE_MESSAGE(state, { messageId, updates }) {
      const index = state.messages.findIndex(msg => msg.messageId === messageId)
      if (index !== -1) {
        state.messages[index] = { ...state.messages[index], ...updates }
      }
    },

    // 群组成员相关
    SET_GROUP_MEMBERS(state, members) {
      state.groupMembers = members.map(member => ({
        userId: member.userId || '',
        nickName: member.nickName || member.nickname || '用户',
        nickname: member.nickname || member.nickName || '用户',
        userAvatar: member.userAvatar || member.avatar || '',
        avatar: member.avatar || member.userAvatar || '',
        role: member.role || 'user'
      }))
    },
    UPDATE_GROUP_MEMBER(state, member) {
      const index = state.groupMembers.findIndex(
        m => m.userId === member.userId
      )
      const normalizedMember = {
        userId: member.userId || '',
        nickName: member.nickName || member.nickname || '用户',
        nickname: member.nickname || member.nickName || '用户',
        userAvatar: member.userAvatar || member.avatar || '',
        avatar: member.avatar || member.userAvatar || '',
        role: member.role || 'user'
      }
      if (index !== -1) {
        state.groupMembers[index] = normalizedMember
      } else {
        state.groupMembers.push(normalizedMember)
      }
    },
    REMOVE_GROUP_MEMBER(state, userId) {
      state.groupMembers = state.groupMembers.filter(m => m.userId !== userId)
    },

    // 禁言相关
    SET_MUTED_USERS(state, users) {
      state.mutedUsers = users
      state.mutedUsersLoaded = true
    },
    ADD_MUTED_USER(state, user) {
      if (!state.mutedUsers.some(u => u.userId === user.userId)) {
        state.mutedUsers.push(user)
      }
      state.mutedUsersLoaded = true
    },
    REMOVE_MUTED_USER(state, userId) {
      state.mutedUsers = state.mutedUsers.filter(u => u.userId !== userId)
      state.mutedUsersLoaded = true
    },
    // 删除单条聊天消息（按 messageId 过滤）
    REMOVE_MESSAGE(state, messageId) {
      state.messages = state.messages.filter(msg => msg.messageId !== messageId)
    },
    SET_CURRENT_USER_MUTED(state, isMuted) {
      state.currentUserMuted = isMuted
    },

    // 公告和产品卡片
    SET_GROUP_NOTICE(state, notice) {
      state.groupNotice = notice
    },
    SET_PRODUCT_CARD_INFO(state, info) {
      state.productCardInfo = info
    },

    // IM状态
    SET_IM_STATUS(state, status) {
      state.liveStatus = { ...state.liveStatus, ...status }
    },

    // 点赞相关
    SET_LIKE_COUNT(state, count) {
      state.likeCount = count
    },
    INCREMENT_LIKE_COUNT(state) {
      state.likeCount++
    },

    // 清空所有数据
    CLEAR_LIVE_DATA(state) {
      state.messages = []
      state.groupMembers = []
      state.mutedUsers = []
      state.mutedUsersLoaded = false
      state.currentUserMuted = false
      state.groupNotice = ''
      state.productCardInfo = null
      state.liveStatus = {
        connected: false,
        groupJoined: false,
        loading: false,
        error: null
      }
      state.likeCount = 0
      state.onlineUsers = []
      state.onlineUserCount = 0
      state.isBigGroup = false
      state.socketStats = {
        uv: 0,
        pv: 0,
        ou: 0,
        liveId: '',
        virtualOnlineCount: 0
      }
      state.messageHistory = {
        nextPageToken: '',
        hasMore: true,
        loading: false,
        initialized: false
      }
    },
    // 房间商品相关
    SET_ROOM_GOODS_TOTAL(state, total) {
      state.roomGoodsTotal = total
    },
    SET_MICROPHONE_VOLUME(state, volume) {
      state.microphoneVolume = volume
    },
    SET_MIRROR_ENABLED(state, enabled) {
      state.isMirrorEnabled = enabled
    },
    SET_ROOM_INFO(state, roomInfo) {
      state.roomInfo = { ...state.roomInfo, ...roomInfo }
    }
  },

  actions: {
    addMessage({ commit }, message) {
      commit('ADD_MESSAGE', message)
    },
    addMessages({ commit }, messages) {
      commit('ADD_MESSAGES', messages)
    },
    updateGroupMembers({ commit }, members) {
      commit('SET_GROUP_MEMBERS', members)
    },
    updateMutedUsers({ commit }, users) {
      commit('SET_MUTED_USERS', users)
    },
    muteUser({ commit }, user) {
      commit('ADD_MUTED_USER', user)
    },
    unmuteUser({ commit }, userId) {
      commit('REMOVE_MUTED_USER', userId)
    },
    removeMessage({ commit }, messageId) {
      commit('REMOVE_MESSAGE', messageId)
    },
    updateIMStatus({ commit }, status) {
      commit('SET_IM_STATUS', status)
    },
    clearLiveData({ commit }) {
      commit('CLEAR_LIVE_DATA')
    }
  }
}
